import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  SvgIconProps,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Label as TagIcon,
  Badge as IdLegalIcon,
  LocalHospital as MedicalIcon,
  School as SchoolIcon,
  AccountBalance as FinancialIcon,
  HealthAndSafety as InsuranceIcon,
  Folder as OtherIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard } from '../../components/common';
import { DocumentUploadDialog } from '../../components/dialogs/DocumentUploadDialog';
import { useFamilyData } from '../../hooks';
import { deleteFamilyData } from '../../services/dataApi';
import { getMediaFilesByFileIdDownloadUrl } from '../../services/api/familypal';
import { colors } from '../../theme';

export interface DocumentData {
  name: string;
  description?: string;
  category: 'id_legal' | 'medical' | 'school' | 'financial' | 'insurance' | 'other';
  tags?: string[];
  file_id?: string;
}

const CATEGORY_CONFIG: Record<
  DocumentData['category'],
  { label: string; icon: React.ComponentType<SvgIconProps>; color: string }
> = {
  id_legal: { label: 'ID & Legal', icon: IdLegalIcon, color: '#5D9CEC' },
  medical: { label: 'Medical', icon: MedicalIcon, color: '#DC5D72' },
  school: { label: 'School', icon: SchoolIcon, color: '#EDA745' },
  financial: { label: 'Financial', icon: FinancialIcon, color: '#A0BC60' },
  insurance: { label: 'Insurance', icon: InsuranceIcon, color: '#AC8EB8' },
  other: { label: 'Other', icon: OtherIcon, color: '#9CA3AF' },
};

const CATEGORY_ORDER: DocumentData['category'][] = [
  'id_legal',
  'medical',
  'school',
  'financial',
  'insurance',
  'other',
];

const DocumentsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<DocumentData['category'] | 'all'>('all');
  const [searchText, setSearchText] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { items, loading, refetch } = useFamilyData<DocumentData>('document');

  const documents = useMemo(() => {
    return items.map((item) => ({
      id: item.id,
      dataId: item.data?.id,
      data: item.data?.data,
      created_at: item.created_at,
      modified_at: item.modified_at,
    }));
  }, [items]);

  const filteredDocuments = useMemo(() => {
    let result = documents;

    if (selectedCategory !== 'all') {
      result = result.filter((doc) => doc.data?.category === selectedCategory);
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (doc) =>
          doc.data?.name?.toLowerCase().includes(lower) ||
          doc.data?.description?.toLowerCase().includes(lower) ||
          doc.data?.tags?.some((tag) => tag.toLowerCase().includes(lower))
      );
    }

    return result;
  }, [documents, selectedCategory, searchText]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: documents.length };
    for (const cat of CATEGORY_ORDER) {
      counts[cat] = documents.filter((d) => d.data?.category === cat).length;
    }
    return counts;
  }, [documents]);

  const handleDownload = async (doc: (typeof documents)[0]) => {
    if (!doc.data?.file_id) {
      enqueueSnackbar('No file attached to this document', { variant: 'warning' });
      return;
    }

    setDownloadingId(doc.id);
    try {
      const response = await getMediaFilesByFileIdDownloadUrl({
        path: { file_id: doc.data.file_id },
      } as any);
      const result = response.data as any;
      const url = result?.download_url || result?.downloadUrl || result?.url;
      if (!url) throw new Error('Failed to get download URL');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to get download URL', { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleEdit = (doc: (typeof documents)[0]) => {
    setSelectedDocument(items.find((i) => i.id === doc.id) || null);
    setUploadDialogOpen(true);
  };

  const handleDelete = async (doc: (typeof documents)[0]) => {
    if (!window.confirm(`Delete "${doc.data?.name}"? This cannot be undone.`)) return;
    try {
      await deleteFamilyData(doc.id);
      enqueueSnackbar('Document deleted', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to delete document', { variant: 'error' });
    }
  };

  const handleUploadClose = () => {
    setUploadDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleUploadSave = async () => {
    enqueueSnackbar(
      selectedDocument ? 'Document updated' : 'Document uploaded',
      { variant: 'success' }
    );
    await refetch();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading && documents.length === 0) {
    return <LoadingSpinner message="Loading documents..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Documents"
        subtitle="Store and organize important family documents"
        actionLabel="Upload Document"
        actionIcon={AddIcon}
        onAction={() => {
          setSelectedDocument(null);
          setUploadDialogOpen(true);
        }}
      />

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {CATEGORY_ORDER.map((cat) => {
          const config = CATEGORY_CONFIG[cat];
          const Icon = config.icon;
          return (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={cat}>
              <DataCard
                title={config.label}
                icon={Icon}
                iconColor={config.color}
                value={categoryCounts[cat] || 0}
                onClick={() =>
                  setSelectedCategory(selectedCategory === cat ? 'all' : cat)
                }
              />
            </Grid>
          );
        })}
      </Grid>

      <Card>
        <CardContent>
          {/* Search and Filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search documents..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/* Category Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={selectedCategory}
              onChange={(_, val) => setSelectedCategory(val)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`All (${categoryCounts.all})`} value="all" />
              {CATEGORY_ORDER.map((cat) => {
                const config = CATEGORY_CONFIG[cat];
                return (
                  <Tab
                    key={cat}
                    label={`${config.label} (${categoryCounts[cat] || 0})`}
                    value={cat}
                    disabled={categoryCounts[cat] === 0}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Document List */}
          {filteredDocuments.length === 0 ? (
            <EmptyState
              icon={DocumentIcon}
              title={searchText || selectedCategory !== 'all' ? 'No documents found' : 'No documents yet'}
              description={
                !searchText && selectedCategory === 'all'
                  ? 'Upload important documents like IDs, medical records, and insurance cards'
                  : undefined
              }
              actionLabel={!searchText && selectedCategory === 'all' ? 'Upload Document' : undefined}
              onAction={
                !searchText && selectedCategory === 'all'
                  ? () => {
                      setSelectedDocument(null);
                      setUploadDialogOpen(true);
                    }
                  : undefined
              }
            />
          ) : (
            <List disablePadding>
              {filteredDocuments.map((doc, idx) => {
                const data = doc.data;
                if (!data) return null;
                const catConfig = CATEGORY_CONFIG[data.category] || CATEGORY_CONFIG.other;
                const Icon = catConfig.icon;
                const isDownloading = downloadingId === doc.id;

                return (
                  <ListItem
                    key={doc.id}
                    divider={idx < filteredDocuments.length - 1}
                    sx={{ px: 0, py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: catConfig.color + '20',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon sx={{ color: catConfig.color, fontSize: 20 }} />
                      </Box>
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" fontWeight={600}>
                            {data.name}
                          </Typography>
                          <Chip
                            label={catConfig.label}
                            size="small"
                            sx={{
                              bgcolor: catConfig.color + '20',
                              color: catConfig.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {data.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                              noWrap
                            >
                              {data.description}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="caption" color="text.disabled">
                              {formatDate(doc.created_at)}
                            </Typography>
                            {data.tags?.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                icon={<TagIcon sx={{ fontSize: '12px !important' }} />}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: '#EEF2FF',
                                  color: '#4F46E5',
                                  '& .MuiChip-icon': { color: '#4F46E5' },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {data.file_id && (
                          <Tooltip title="Download / View">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(doc)}
                              disabled={isDownloading}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(doc)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(doc)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Upload / Edit Dialog */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={handleUploadClose}
        onSave={handleUploadSave}
        document={selectedDocument}
      />
    </Box>
  );
};

export default DocumentsPage;
